import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { PRIMARY_HERO_HEIGHT } from '../../../../../shared/components/v3/Heros/heroSizes';
import StyledTable from '../../../../../shared/components/v3/Table/StyledTable/StyledTable';
import TablePaginationV3 from '../../../../../shared/components/v3/TablePagination/TablePagination';
import Toast from '../../../../../shared/components/v3/Toast/Toast';
import { DEFAULT_CHALLENGE } from '../../../../../shared/constants/challenges';
import { DEFAULT_PAGINATOR_OPTIONS_V3, DEFAULT_PAGINATOR_STATE_V3 } from '../../../../../shared/constants/constants';
import {
  CHALLENGE_DETAIL,
  CLICK_ACTION,
  DELETE_CHALLENGE_SUCCESS,
  SECTION_CHALLENGES
} from '../../../../../shared/constants/tagManager';
import { DELETE_CHALLENGE_SUCCESS_TOAST, CREATE_CHALLENGE_SUCCESS_TOAST } from '../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { CHALLENGES_TABLE_COLUMNS } from '../../../../../shared/handlers/tableColumns';
import { useIsMounted } from '../../../../../shared/hooks/useIsMounted';
import { IChallengeDetail } from '../../../../../shared/interfaces/challenges';
import { ITableFilter } from '../../../../../shared/interfaces/tables';
import Challenges from '../../../../../shared/services/api/challenges/challenges';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_CHALLENGES_MAIN } from '../../../../../shared/translations/challenges';
import { getFromLocalStorage, sortArray, toLocalStorage } from '../../../../../shared/utils/utils';
import ChallengeDetail from '../ChallengeDetail/ChallengeDetail';
import ChallengeFilters from '../ChallengesFilter/ChallengeFilters';

const ChallengesTable = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [challenges, setChallenges] = useState<IChallengeDetail[]>([]);
  const [filteredChallenges, setFilteredChallenges] = useState<IChallengeDetail[]>([]);
  const [currentPageData, setCurrentPageData] = useState<IChallengeDetail[]>([]);
  const [selectedChallenge, setSelectedChallenge] = useState<IChallengeDetail>(DEFAULT_CHALLENGE);
  const [filters, setFilters] = useState<ITableFilter>();
  const [error, setError] = useState(false);
  const [activeRow, setActiveRow] = useState<string | undefined>(undefined);
  const [openDeleteToast, setOpenDeleteToast] = useState<boolean>(false);
  const [openCreateToast, setOpenCreateToast] = useState<boolean>(false);
  const [paginatorState, setPaginatorState] = useState<number>(150);
  const [actualPage, setActualPage] = useState<number>(0);
  const [scrollDown, setScrollDown] = useState<boolean>(false);
  const { company } = useContext(CompanyContext);

  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const onSearch = useCallback(
    async (searchValue?: string, filterValue?: string) => {
      if (!isMounted) return;
      try {
        setLoading(true);
        setError(false);
        setOpen(false);
        setSelectedChallenge(DEFAULT_CHALLENGE);
        setActiveRow(undefined);
        setFilters({ searchValue, filterValue });
        if (company.id !== '') {
          const response = await Challenges.getCompanyChallenges(company.id);
          if (isMounted) {
            setChallenges(response);
            setFilteredChallenges(response);
          }
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [company, isMounted]
  );

  const handleTabChange = (status: string) => {
    setActualPage(0);
    setPaginatorState(DEFAULT_PAGINATOR_STATE_V3);
    if (status == 'all') {
      setFilteredChallenges(challenges);
    } else {
      const filtered = challenges.filter((challenge) => challenge.status === status);
      setFilteredChallenges(filtered);
    }
    paginateChallenges();
  };

  const searchOnError = useCallback(() => {
    onSearch(filters?.searchValue, filters?.filterValue);
  }, [onSearch, filters]);

  const onRowClick = (challenge: IChallengeDetail) => {
    TagManagerService.pushEvent(CLICK_ACTION, CHALLENGE_DETAIL);
    if (!open) {
      setActiveRow(challenge.id);
      setSelectedChallenge(challenge);
      setOpen(true);
    }
  };

  const sortData = (columnKey: string, isAscending: boolean) => {
    if (columnKey === 'period') {
      sortArray(filteredChallenges, 'startAt', isAscending);
    } else {
      sortArray(filteredChallenges, columnKey, isAscending);
    }
    paginateChallenges();
  };

  const onDelete = (challengeId: string) => {
    const newChallenges = challenges.filter((challenge) => challenge.id !== challengeId);
    setFilteredChallenges(newChallenges);
    setChallenges(newChallenges);
    TagManagerService.pushEvent(CLICK_ACTION, DELETE_CHALLENGE_SUCCESS);
    setOpenDeleteToast(true);
  };

  const getTotalChallenges = useCallback(() => {
    return filteredChallenges ? filteredChallenges.length : 0;
  }, [filteredChallenges]);

  const paginateChallenges = useCallback(() => {
    const from = actualPage * paginatorState;
    const to = from + paginatorState;
    const pageData = filteredChallenges?.slice(from, to);
    setCurrentPageData(pageData || []);
    setLoading(false);
  }, [actualPage, paginatorState, filteredChallenges]);

  const handleClose = () => {
    setActiveRow(undefined);
    setOpen(false);
  };

  const handleCloseDeleteToast = () => {
    setOpenDeleteToast(false);
  };

  const handleCloseCreateToast = () => {
    setOpenCreateToast(false);
  };

  useEffect(() => {
    const totalChallenges = getTotalChallenges();

    if (totalChallenges) {
      paginateChallenges();
    }
  }, [filteredChallenges, getTotalChallenges, paginateChallenges]);

  useEffect(() => {
    paginateChallenges();
  }, [actualPage, paginateChallenges, paginatorState]);

  useEffect(() => {
    setActualPage(0);
  }, [filteredChallenges, paginatorState]);

  useEffect(() => {
    const totalChallenges = getTotalChallenges();

    if (totalChallenges) {
      paginateChallenges();
    }
  }, [filteredChallenges, getTotalChallenges, paginateChallenges]);

  useEffect(() => {
    paginateChallenges();
  }, [actualPage, paginateChallenges, paginatorState]);

  useEffect(() => {
    onSearch();
  }, [onSearch]);

  useEffect(() => {
    const challengeCreated = getFromLocalStorage('challengeCreated');
    if (challengeCreated == 'true') {
      setOpenCreateToast(true);
      toLocalStorage('challengeCreated', 'false');
    }
  }, []);

  useEffect(() => {
    if (scrollDown) {
      window.scrollTo({ top: document.body.scrollHeight });
      setScrollDown(false);
    }
  }, [currentPageData]);

  return (
    <>
      <ChallengeFilters onFilterChange={handleTabChange} />
      <StyledTable
        data={currentPageData}
        columns={CHALLENGES_TABLE_COLUMNS}
        top={PRIMARY_HERO_HEIGHT}
        error={error}
        loading={loading}
        errorAction={searchOnError}
        activeRow={activeRow}
        onRowClick={onRowClick}
        sortData={sortData}
        onChange={onDelete}
        emptyCard={{
          primaryText: TRANSLATIONS_CHALLENGES_MAIN.table.emptyData.title,
          secondaryText: TRANSLATIONS_CHALLENGES_MAIN.table.emptyData.description
        }}
        noFilterDataCard={{
          primaryText: TRANSLATIONS_CHALLENGES_MAIN.table.noSearch.title,
          secondaryText: TRANSLATIONS_CHALLENGES_MAIN.table.noSearch.description
        }}
      />
      {filteredChallenges.length > 150 && filteredChallenges && (
        <div className="paginator-container">
          <TablePaginationV3
            rowsPerPageOptions={DEFAULT_PAGINATOR_OPTIONS_V3}
            rowsPerPage={paginatorState}
            count={getTotalChallenges()}
            actualPage={actualPage}
            onPageChange={(e) => {
              setActualPage(e);
              setScrollDown(true);
            }}
            onRowsPerPageChange={(e) => {
              setPaginatorState(e);
              setScrollDown(true);
            }}
            sectionPage={SECTION_CHALLENGES}
            marginTop={20}
          />
        </div>
      )}
      <ChallengeDetail open={open} challenge={selectedChallenge} onClose={handleClose} />
      <Toast
        title={t(DELETE_CHALLENGE_SUCCESS_TOAST.title)}
        icon={DELETE_CHALLENGE_SUCCESS_TOAST.icon}
        open={openDeleteToast}
        button={DELETE_CHALLENGE_SUCCESS_TOAST.button}
        buttonAction={handleCloseDeleteToast}
        handleClose={handleCloseDeleteToast}
      />
      <Toast
        title={t(CREATE_CHALLENGE_SUCCESS_TOAST.title)}
        icon={CREATE_CHALLENGE_SUCCESS_TOAST.icon}
        open={openCreateToast}
        button={CREATE_CHALLENGE_SUCCESS_TOAST.button}
        buttonAction={handleCloseCreateToast}
        handleClose={handleCloseCreateToast}
      />
    </>
  );
};

export default ChallengesTable;
